import React, { useEffect, useRef, useState } from 'react';

const BACKEND = import.meta.env.VITE_API_URL || 'https://api.cloudairambo.com';

export default function ScanSessionPage({ sessionId }: { sessionId: string }) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const cooldown = useRef(false);
  const lastVal = useRef('');
  const startingRef = useRef(false);
  const zxingReaderRef = useRef<any>(null);
  const zxingActiveRef = useRef(false);
  const [theme, setTheme] = useState<'dark' | 'light'>('dark');
  const [status, setStatus] = useState('Initializing scanner...');
  const [mode, setMode] = useState<'barcode' | 'label'>('barcode');
  const [labelImage, setLabelImage] = useState('');
  const [labelOcrLoading, setLabelOcrLoading] = useState(false);
  const [labelParsed, setLabelParsed] = useState<{name:string;price:string;qty:string}|null>(null);
  const labelVideoRef = useRef<HTMLVideoElement>(null);
  const labelCanvasRef = useRef<HTMLCanvasElement>(null);
  const [labelCameraActive, setLabelCameraActive] = useState(false);
  const [detected, setDetected] = useState('');
  const [error, setError] = useState('');
  const [scanning, setScanning] = useState(false);
  const [pulse, setPulse] = useState(false);

  const sendToDesktop = async (val: string, labelData?: { type: string; name: string; price: string; qty: string }) => {
    console.log('[PhoneScan] Sending to desktop:', labelData || val);
    try {
      const res = await fetch(`${BACKEND}/scan-session/${sessionId}`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(labelData || { value: val }),
      });
      console.log('[PhoneScan] Backend response:', res.status);
    } catch (err) {
      console.error('[PhoneScan] Failed to send to desktop:', err);
      setStatus('⚠️ Could not reach desktop. Same WiFi?');
    }
  };

  // Tell desktop the phone opened the page — before any barcode is scanned
  useEffect(() => {
    fetch(`${BACKEND}/scan-session/${sessionId}`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ type: 'connected' }),
    }).catch(() => {});
  }, [sessionId]);

  useEffect(() => {
    let interval: ReturnType<typeof setInterval>;
    let destroyed = false;

    console.log('[PhoneScan] Component mounted, sessionId:', sessionId);

    const startZxingLoop = async () => {
      if (zxingActiveRef.current || destroyed) return;
      zxingActiveRef.current = true;
      try {
        const { BrowserMultiFormatReader } = await import('@zxing/browser');
        const reader = new BrowserMultiFormatReader();
        zxingReaderRef.current = reader;

        const loop = async () => {
          while (zxingActiveRef.current && !destroyed && videoRef.current) {
            try {
              const result = await reader.decodeOnceFromVideoElement(videoRef.current);
              const val = result?.getText();
              if (val && !cooldown.current && val !== lastVal.current) {
                lastVal.current = val;
                cooldown.current = true;
                console.log('[PhoneScan] (zxing) Barcode detected:', val);
                setDetected(val);
                setPulse(true);
                setStatus(`✅ Added: ${val}`);
                await sendToDesktop(val);
                setTimeout(() => {
                  cooldown.current = false;
                  lastVal.current = '';
                  setDetected('');
                  setPulse(false);
                }, 1500);
              }
            } catch {
              await new Promise(r => setTimeout(r, 100));
            }
          }
        };
        loop();
      } catch (err) {
        console.error('[PhoneScan] zxing failed to load:', err);
        setStatus('Auto-scan unavailable. Type manually below.');
      }
    };

    const stopZxingLoop = () => {
      zxingActiveRef.current = false;
      zxingReaderRef.current = null;
    };

    if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
      console.warn('[PhoneScan] Camera API not available');
      setStatus('Camera not available. Type manually below.');
      setError('Camera API unavailable — use HTTPS or type manually.');
      return;
    }

    const startStream = async () => {
      if (destroyed || startingRef.current) return;
      startingRef.current = true;
      console.log('[PhoneScan] Starting camera stream...');
      try {
        // Stop any existing stream first
        if (streamRef.current) {
          console.log('[PhoneScan] Stopping old stream tracks');
          streamRef.current.getTracks().forEach(t => t.stop());
          streamRef.current = null;
        }

        const stream = await navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
        });
        console.log('[PhoneScan] Camera stream acquired:', stream.id);
        streamRef.current = stream;

        if (videoRef.current) {
          videoRef.current.srcObject = null;
          videoRef.current.srcObject = stream;
          try {
            await videoRef.current.play();
            console.log('[PhoneScan] Video playing');
          } catch (playErr: any) {
            if (playErr.name === 'AbortError') {
              console.log('[PhoneScan] play() aborted by a newer load — safe to ignore');
            } else {
              throw playErr;
            }
          }
        }

        if (!destroyed) {
          setScanning(true);
          setStatus('Point camera at barcode or QR code');
        }

        if ('BarcodeDetector' in window) {
          console.log('[PhoneScan] BarcodeDetector available — starting scan loop');
          // @ts-ignore
          const detector = new BarcodeDetector({
            formats: ['code_128', 'code_39', 'qr_code', 'ean_13', 'ean_8', 'data_matrix', 'upc_a', 'upc_e']
          });

          interval = setInterval(async () => {
            if (!videoRef.current || cooldown.current || destroyed || mode === 'label') return;
            try {
              const codes = await detector.detect(videoRef.current);
              if (codes.length > 0) {
                const val = codes[0].rawValue;
                console.log('[PhoneScan] Barcode detected:', val, '| last:', lastVal.current);

                if (val === lastVal.current) {
                  console.log('[PhoneScan] Same barcode still in frame — ignoring');
                  return;
                }

                lastVal.current = val;
                cooldown.current = true;
                console.log('[PhoneScan] New scan! Cooldown ON');

                setDetected(val);
                setPulse(true);
                setStatus(`✅ Added: ${val}`);
                await sendToDesktop(val);

                setTimeout(async () => {
                  if (destroyed) return;
                  console.log('[PhoneScan] Cooldown OFF — restarting stream for next scan');
                  cooldown.current = false;
                  lastVal.current = '';
                  setDetected('');
                  setPulse(false);

                  // Clear interval before restarting
                  clearInterval(interval);
                  await startStream();
                }, 1500);
              }
            } catch (err) {
              // Normal — no barcode in frame
            }
          }, 150);
        } else {
          console.warn('[PhoneScan] BarcodeDetector NOT available — falling back to zxing');
          setStatus('Point camera at barcode or QR code');
          startZxingLoop();
        }
      } catch (err: any) {
        console.error('[PhoneScan] Camera error:', err);
        if (err.name === 'AbortError') {
          startingRef.current = false;
          setTimeout(() => { if (!destroyed) startStream(); }, 300);
          return;
        }
        if (err.name === 'NotAllowedError') {
          setStatus('Camera denied — check permissions.');
        } else {
          setStatus('Camera error — type manually below.');
        }
        setError(String(err));
      } finally {
        startingRef.current = false;
      }
    };

    startStream();

    return () => {
      console.log('[PhoneScan] Cleanup — stopping stream');
      destroyed = true;
      stopZxingLoop();
      clearInterval(interval);
      streamRef.current?.getTracks().forEach(t => t.stop());
      if (videoRef.current) videoRef.current.srcObject = null;
    };
  }, [sessionId]);

 return (
    <div style={{
      minHeight: '100vh',
      background: theme === 'dark' ? '#0a0a0f' : '#f8f8ff',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      padding: '24px 20px',
      fontFamily: 'ui-sans-serif, system-ui, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
      position: 'relative',
      overflow: 'hidden',
    }}>

      {/* Animated background grid */}
      <div style={{
        position: 'absolute', inset: 0,
        backgroundImage: `linear-gradient(${theme === 'dark' ? 'rgba(99,102,241,0.07)' : 'rgba(99,102,241,0.12)'} 1px, transparent 1px), linear-gradient(90deg, ${theme === 'dark' ? 'rgba(99,102,241,0.07)' : 'rgba(99,102,241,0.12)'} 1px, transparent 1px)`,
        backgroundSize: '40px 40px',
        animation: 'gridMove 8s linear infinite',
      }} />

      {/* Glowing orb top */}
      <div style={{
        position: 'absolute', top: '-120px', left: '50%', transform: 'translateX(-50%)',
        width: '400px', height: '400px',
        background: 'radial-gradient(circle, rgba(99,102,241,0.25) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      {/* Glowing orb bottom */}
      <div style={{
        position: 'absolute', bottom: '-100px', right: '-100px',
        width: '300px', height: '300px',
        background: 'radial-gradient(circle, rgba(16,185,129,0.15) 0%, transparent 70%)',
        pointerEvents: 'none',
      }} />

      <style>{`
        @keyframes gridMove { 0% { transform: translateY(0); } 100% { transform: translateY(40px); } }
        @keyframes scanLine { 0% { top: 8px; opacity: 1; } 50% { opacity: 0.6; } 100% { top: calc(100% - 8px); opacity: 1; } }
        @keyframes cornerPulse { 0%, 100% { opacity: 1; } 50% { opacity: 0.4; } }
        @keyframes fadeSlideUp { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
        @keyframes successPop { 0% { transform: scale(0.5); opacity: 0; } 60% { transform: scale(1.15); } 100% { transform: scale(1); opacity: 1; } }
        @keyframes ripple { 0% { transform: scale(1); opacity: 0.6; } 100% { transform: scale(2.5); opacity: 0; } }
        @keyframes statusBlink { 0%, 100% { opacity: 1; } 50% { opacity: 0.5; } }
        .scan-corner { position: absolute; width: 22px; height: 22px; animation: cornerPulse 2s ease-in-out infinite; }
        .logo-badge { animation: fadeSlideUp 0.6s ease both; }
        .scanner-wrap { animation: fadeSlideUp 0.6s ease 0.2s both; }
        .status-text { animation: fadeSlideUp 0.6s ease 0.3s both; }
        .input-wrap { animation: fadeSlideUp 0.6s ease 0.4s both; }
        .success-wrap { animation: successPop 0.5s cubic-bezier(0.175, 0.885, 0.32, 1.275) both; }
      `}</style>

      {/* Logo / Brand */}
      <div className="logo-badge" style={{
        display: 'flex', alignItems: 'center', gap: 10, marginBottom: 28,
        position: 'relative', zIndex: 1,
      }}>
        <div style={{
          width: 38, height: 38,
          background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
          borderRadius: 10,
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          fontSize: 16, fontWeight: 900, color: '#fff',
          boxShadow: '0 0 20px rgba(99,102,241,0.5)',
          fontFamily: 'Space Mono, monospace',
        }}>CA</div>
        <div>
          <div style={{ color: '#fff', fontSize: 15, fontWeight: 700, letterSpacing: '-0.3px', lineHeight: 1.2 }}>
            CloudAI<span style={{ color: '#6366f1' }}>Rambo</span>
          </div>
          <div style={{ color: theme === 'dark' ? '#4b5563' : '#6b7280', fontSize: 10, fontWeight: 500, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Space Mono, monospace' }}>
            Barcode Scanner
          </div>
        </div>
      </div>

      <button onClick={() => setTheme(t => t === 'dark' ? 'light' : 'dark')} style={{
        position: 'absolute', top: 20, right: 20, zIndex: 10,
        background: theme === 'dark' ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)',
        border: `1px solid ${theme === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`,
        borderRadius: 20, padding: '6px 14px', cursor: 'pointer',
        fontSize: 16, color: theme === 'dark' ? '#fff' : '#000',
      }}>
        {theme === 'dark' ? '☀️' : '🌙'}
      </button>

{/* Mode toggle */}
<div style={{
  display: 'flex', gap: 6, marginBottom: 16, zIndex: 1, position: 'relative',
  background: 'rgba(255,255,255,0.05)', borderRadius: 10, padding: 4, width: '100%', maxWidth: 320,
}}>
  {(['barcode', 'label'] as const).map(m => (
    <button key={m} onClick={() => {
      setMode(m);
      setLabelImage('');
      setLabelParsed(null);

      if (m === 'label') {
        streamRef.current?.getTracks().forEach(t => t.stop());
        streamRef.current = null;
        if (videoRef.current) videoRef.current.srcObject = null;
        setScanning(false);
        setStatus('Take a photo of the label');
      } else {
        if (labelVideoRef.current?.srcObject) {
          (labelVideoRef.current.srcObject as MediaStream).getTracks().forEach(t => t.stop());
          labelVideoRef.current.srcObject = null;
        }
        setLabelCameraActive(false);
        setStatus('Initializing scanner...');
        navigator.mediaDevices.getUserMedia({
          video: { facingMode: 'environment', width: { ideal: 1280 }, height: { ideal: 720 } }
        }).then(stream => {
          streamRef.current = stream;
          if (videoRef.current) {
            videoRef.current.srcObject = stream;
            videoRef.current.play();
          }
          setScanning(true);
          setStatus('Point camera at barcode or QR code');
        }).catch(() => setStatus('Camera denied'));
      }
    }}
    style={{
      flex: 1, padding: '8px', border: 'none', borderRadius: 8, cursor: 'pointer',
      fontWeight: 700, fontSize: 12,
      background: mode === m ? 'linear-gradient(135deg,#6366f1,#8b5cf6)' : 'transparent',
      color: mode === m ? '#fff' : '#6b7280',
      boxShadow: mode === m ? '0 2px 10px rgba(99,102,241,0.4)' : 'none',
    }}>
      {m === 'barcode' ? '📷 Scan Barcode' : '🏷️ Read Label'}
    </button>
  ))}
</div>
 
      {/* Scanner always visible — detected state just shows flash overlay */}


      <div className="scanner-wrap" style={{ position: 'relative', width: '100%', maxWidth: 320, zIndex: 1 }}>
        <div style={{
          background: 'rgba(255,255,255,0.03)',
          border: `1px solid ${detected ? 'rgba(16,185,129,0.5)' : 'rgba(99,102,241,0.2)'}`,
          borderRadius: 20,
          padding: 12,
          backdropFilter: 'blur(10px)',
          boxShadow: detected
            ? '0 0 40px rgba(16,185,129,0.2), inset 0 1px 0 rgba(255,255,255,0.05)'
            : '0 0 40px rgba(99,102,241,0.1), inset 0 1px 0 rgba(255,255,255,0.05)',
          transition: 'border 0.3s, box-shadow 0.3s',
        }}>
          <div style={{ position: 'relative', borderRadius: 12, overflow: 'hidden', aspectRatio: '1', background: '#0f0f1a' }}>
            <video
              ref={videoRef}
              autoPlay playsInline muted
              style={{ width: '100%', height: '100%', objectFit: 'cover', display: mode === 'barcode' ? 'block' : 'none' }}
            />
            {mode === 'label' && (
              <video
                ref={labelVideoRef}
                autoPlay playsInline muted
                style={{ width: '100%', height: '100%', objectFit: 'cover', display: labelCameraActive ? 'block' : 'none' }}
              />
            )}
            {mode === 'label' && !labelCameraActive && (
              <div style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <button onClick={async () => {
                  setLabelCameraActive(true);
                  try {
                    const stream = await navigator.mediaDevices.getUserMedia({ video: { facingMode: 'environment' } });
                    requestAnimationFrame(() => {
                      if (labelVideoRef.current) {
                        labelVideoRef.current.srcObject = stream;
                        labelVideoRef.current.play().catch(err => console.error('[Label] play() failed:', err));
                      }
                    });
                  } catch (err) {
                    console.error('[Label] getUserMedia failed:', err);
                    setLabelCameraActive(false);
                    setStatus('Camera denied — check permissions');
                  }
                }} style={{
                  padding: '14px 20px', borderRadius: 12, border: '2px dashed rgba(99,102,241,0.4)',
                  background: 'rgba(99,102,241,0.08)', color: '#a5b4fc',
                  fontWeight: 700, fontSize: 13, cursor: 'pointer',
                }}>📷 Take Photo of Label</button>
              </div>
            )}
            {mode === 'label' && labelCameraActive && (
              <button onClick={async () => {
                const video = labelVideoRef.current;
                if (!video) return;
                const canvas = document.createElement('canvas');
                canvas.width = video.videoWidth;
                canvas.height = video.videoHeight;
                canvas.getContext('2d')?.drawImage(video, 0, 0);
                const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
                setLabelImage(dataUrl);
                (video.srcObject as MediaStream)?.getTracks().forEach(t => t.stop());
                video.srcObject = null;
                setLabelCameraActive(false);
                setLabelOcrLoading(true);
                setLabelParsed(null);
                try {
                  const Tesseract = await import('tesseract.js');
                  const result = await Tesseract.recognize(dataUrl, 'eng', { logger: () => {} });
                  const text = result.data.text.trim();
                  const lines = text.split('\n').map((l: string) => l.trim()).filter(Boolean);
                  let name = ''; let price = ''; let qty = '1';
                  for (const line of lines) {
                    const pm = line.match(/(?:₹|Rs\.?|MRP|Price|rate|amt)[:\s]*([0-9]+(?:\.[0-9]{1,2})?)/i);
                    if (pm && !price) { price = pm[1]; continue; }
                    const qm = line.match(/(?:qty|pcs|nos|pack)[:\s]*([0-9]+)/i);
                    if (qm && qty === '1') { qty = qm[1]; continue; }
                    if (!name && line.length > 2 && !/^[0-9\s.,\-]+$/.test(line) && !/(?:barcode|batch|exp|mfg|date|gstin|tel|ph:|www\.|@)/i.test(line)) name = line;
                  }
                  setLabelParsed({ name: name || lines[0] || '', price, qty });
                } catch { setLabelParsed({ name: '', price: '', qty: '1' }); }
                finally { setLabelOcrLoading(false); }
              }} style={{
                position: 'absolute', bottom: 12, left: '50%', transform: 'translateX(-50%)',
                background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', border: 'none',
                borderRadius: 30, padding: '10px 28px', color: '#fff',
                fontWeight: 800, fontSize: 13, cursor: 'pointer', zIndex: 5,
              }}>📸 Capture</button>
            )}

            {/* Dark overlay vignette */}
            <div style={{
              position: 'absolute', inset: 0,
              background: 'radial-gradient(ellipse at center, transparent 55%, rgba(0,0,0,0.7) 100%)',
              pointerEvents: 'none',
            }} />

            {/* Success flash overlay */}
            {detected && (
              <div style={{
                position: 'absolute', inset: 0,
                background: 'rgba(16,185,129,0.15)',
                display: 'flex', flexDirection: 'column',
                alignItems: 'center', justifyContent: 'center',
                gap: 8,
                pointerEvents: 'none',
              }}>
                <div style={{ fontSize: 40 }}>✅</div>
                <div style={{ color: '#10b981', fontWeight: 800, fontSize: 13, fontFamily: 'Space Mono, monospace', textAlign: 'center', padding: '0 12px', wordBreak: 'break-all' }}>
                  {detected}
                </div>
              </div>
            )}

            {/* Scan line */}
            {scanning && !detected && (
              <div style={{
                position: 'absolute', left: 16, right: 16, height: 2,
                background: 'linear-gradient(90deg, transparent, #6366f1, #10b981, #6366f1, transparent)',
                borderRadius: 1,
                animation: 'scanLine 2s ease-in-out infinite',
                boxShadow: '0 0 8px rgba(99,102,241,0.8)',
                pointerEvents: 'none',
              }} />
            )}

            {/* Corner brackets */}
            {[
              { top: 10, left: 10, borderTop: '2px solid #6366f1', borderLeft: '2px solid #6366f1', borderRadius: '6px 0 0 0' },
              { top: 10, right: 10, borderTop: '2px solid #6366f1', borderRight: '2px solid #6366f1', borderRadius: '0 6px 0 0' },
              { bottom: 10, left: 10, borderBottom: '2px solid #6366f1', borderLeft: '2px solid #6366f1', borderRadius: '0 0 0 6px' },
              { bottom: 10, right: 10, borderBottom: '2px solid #6366f1', borderRight: '2px solid #6366f1', borderRadius: '0 0 6px 0' },
            ].map((s, i) => (
              <div key={i} className="scan-corner" style={{ ...s, animationDelay: `${i * 0.15}s` }} />
            ))}

            {/* Center crosshair */}
            <div style={{
              position: 'absolute', top: '50%', left: '50%',
              transform: 'translate(-50%, -50%)',
              width: 40, height: 40,
              border: '1px solid rgba(99,102,241,0.4)',
              borderRadius: 4,
              pointerEvents: 'none',
            }} />
          </div>
        </div>

        {/* Status pill */}
        <div className="status-text" style={{
          marginTop: 14, textAlign: 'center',
          display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 7,
        }}>
          <div style={{
            width: 7, height: 7, borderRadius: '50%',
            background: detected ? '#10b981' : scanning ? '#10b981' : '#6366f1',
            boxShadow: `0 0 6px ${detected ? '#10b981' : '#10b981'}`,
            animation: 'statusBlink 1.5s ease-in-out infinite',
            flexShrink: 0,
          }} />
          <span style={{ color: detected ? '#10b981' : theme === 'dark' ? '#9ca3af' : '#6b7280', fontSize: 13, fontWeight: 500 }}>{status}</span>
        </div>

        {error && (
          <div style={{
            marginTop: 10,
            background: 'rgba(239,68,68,0.1)',
            border: '1px solid rgba(239,68,68,0.2)',
            borderRadius: 8, padding: '8px 12px',
            color: '#f87171', fontSize: 11, textAlign: 'center', wordBreak: 'break-all',
          }}>{error}</div>
        )}
      </div>

{/* Label mode UI */}
      {mode === 'label' && (
        <div style={{ width: '100%', maxWidth: 320, zIndex: 1, display: 'flex', flexDirection: 'column', gap: 10, marginBottom: 16 }}>
          {labelOcrLoading && (
            <div style={{ color: '#9ca3af', fontSize: 12, textAlign: 'center' }}>Reading label...</div>
          )}

          {labelParsed && !labelOcrLoading && (
            <div style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 12, padding: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
              {[
                { label: 'ITEM NAME', key: 'name', type: 'text', color: '#f9fafb' },
                { label: 'PRICE', key: 'price', type: 'number', color: '#67e8f9' },
                { label: 'QTY', key: 'qty', type: 'number', color: '#f9fafb' },
              ].map(({ label, key, type, color }) => (
                <div key={key}>
                  <div style={{ fontSize: 9, color: '#6b7280', fontWeight: 700, marginBottom: 3 }}>{label}</div>
                  <input value={(labelParsed as any)[key]} type={type}
                    onChange={e => setLabelParsed(p => p ? { ...p, [key]: e.target.value } : p)}
                    style={{ width: '100%', padding: '8px 10px', background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(99,102,241,0.25)', borderRadius: 8, fontSize: 13, color, outline: 'none', boxSizing: 'border-box', fontFamily: 'Space Mono, monospace' }} />
                </div>
              ))}
              <button onClick={async () => {
                if (!labelParsed.name.trim()) return;
                setStatus('✅ Sending to desktop...');
                await sendToDesktop('', { type: 'label', name: labelParsed.name.trim(), price: labelParsed.price, qty: labelParsed.qty });
                setLabelParsed(null);
                setLabelImage('');
                setTimeout(() => setStatus('Ready — take another photo'), 1500);
              }} style={{
                padding: '11px', borderRadius: 10, border: 'none',
                background: 'linear-gradient(135deg,#6366f1,#8b5cf6)', color: '#fff',
                fontWeight: 800, fontSize: 13, cursor: 'pointer', marginTop: 4,
              }}>➕ Send to Desktop</button>
            </div>
          )}
        </div>
      )}

      {/* Divider */}
      <div className="input-wrap" style={{
        width: '100%', maxWidth: 320, zIndex: 1,
        display: 'flex', alignItems: 'center', gap: 10, margin: '20px 0 14px',
      }}>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
        <span style={{ color: theme === 'dark' ? '#374151' : '#9ca3af', fontSize: 11, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', fontFamily: 'Space Mono, monospace' }}>or type</span>
        <div style={{ flex: 1, height: 1, background: 'rgba(255,255,255,0.07)' }} />
      </div>

      {/* Manual input */}
      <div className="input-wrap" style={{ width: '100%', maxWidth: 320, zIndex: 1, position: 'relative' }}>
        <input
          type="text"
          placeholder="Type serial / barcode + Enter"
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '13px 16px', borderRadius: 12,
            border: '1px solid rgba(99,102,241,0.25)',
            background: theme === 'dark' ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.04)',
color: theme === 'dark' ? '#f9fafb' : '#111', fontSize: 14,
            fontFamily: 'Space Mono, monospace',
            outline: 'none', backdropFilter: 'blur(8px)',
            transition: 'border-color 0.2s, box-shadow 0.2s',
          }}
          onFocus={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.6)'; e.currentTarget.style.boxShadow = '0 0 0 3px rgba(99,102,241,0.1)'; }}
          onBlur={e => { e.currentTarget.style.borderColor = 'rgba(99,102,241,0.25)'; e.currentTarget.style.boxShadow = 'none'; }}
          onKeyDown={async (e) => {
            const val = (e.target as HTMLInputElement).value.trim();
            if (e.key === 'Enter' && val) {
              console.log('[PhoneScan] Manual entry:', val);
              (e.target as HTMLInputElement).value = '';
              setDetected(val);
              setStatus(`✅ Sent: ${val}`);
              await sendToDesktop(val);
              setTimeout(() => {
                setDetected('');
                setStatus('Point camera at barcode or QR code');
              }, 1500);
            }
          }}
        />
      </div>

      {/* Session ID badge */}
      <div style={{
        marginTop: 24, zIndex: 1,
        display: 'flex', alignItems: 'center', gap: 6,
        background: 'rgba(255,255,255,0.03)',
        border: '1px solid rgba(255,255,255,0.06)',
        borderRadius: 20, padding: '5px 12px',
      }}>
        <div style={{ width: 5, height: 5, borderRadius: '50%', background: '#6366f1', boxShadow: '0 0 4px #6366f1' }} />
        <span style={{ color: theme === 'dark' ? '#4b5563' : '#9ca3af', fontSize: 10, fontFamily: 'Space Mono, monospace', letterSpacing: '0.05em' }}>
          SESSION: {sessionId}
        </span>
      </div>
    </div>
  );
}
